// Agent ticket management: full detail, public replies + internal notes, status
// / label / priority / assignment / escalation controls, GitHub issue linking,
// the development-stage timeline, the AI agent panels (instant reply,
// conversation, coding agent + engineering bridge) and the audit trail.
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { DIALOG_COLORS, Combobox } from "../../../components/ui";
import { brand as themeBrand } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import { useUserStore } from "../../../store/userStore";
import { authenticateUser } from "../../../pages/login/functions";
import { useAlert } from "../../../context/AlertContext";
import { useIsReseller } from "../../../hooks/useIsReseller";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import {
  getTicket, updateTicket, deleteTicket, addAgentComment, linkGithubIssue, linkExistingGithubIssue,
  listGithubRepositories,
  getSettings, getAssignableUsers, formatDate, allowedNextStatuses,
  releaseAiAgent, approveAiAgent, revokeAiAgentApproval, canRetryAiAgent, getAiStaffStatusDetail,
  aiAgentNeedsApproval, isAiAgentApproved, AI_AGENT_META, AI_FIRST_REPLY_META,
  AI_CONVERSATION_HANDOFF_LABEL,
  setTicketStage, selectableStages, hasStageLinks, stageLabelOf,
  retryEngineeringBridge, canRetryEngineeringBridge, engineeringBridgeNeedsRepo, ENGINEERING_BRIDGE_META,
  type Ticket, type Comment, type AuditEntry, type ServiceDeskSettings,
  type AssignableUser, type TicketPriority, type GithubRepository, type AiFirstReplyState,
  type DevStageId,
} from "../services/serviceDeskService";
import { apiErrorMessage } from "../services/envelope";
import { SlaBadge, PriorityBadge, AiAgentBadge, ReworkChip, ChannelChip, SampleChip, isSampleTicket } from "../components/Badges";
import { canCloseTicket, formatClosureDate, formatDurationMs, CLOSED_STATUS } from "../services/ticketClosure";
import { StageTimeline, StageBadge } from "../components/StageTimeline";
import { CommentThread } from "../components/CommentThread";
import { DeskLoadErrorPanel, useDeskLoadError } from "../components/DeskLoadError";
import { AgentSelector } from "../components/AgentSelector";
import { SERVICE_DESK_BASE } from "../config";
import { MODULE_OPTIONS } from "../moduleOptions";
import {
  trackTicketStatusChanged,
  trackTicketStatusChangeFailed,
  trackTicketPriorityChanged,
  trackTicketAssigned,
  trackTicketEscalationChanged,
  trackTicketReplied,
  trackTicketDeleted,
  trackTicketGithubLinked,
} from "../../../analytics/serviceDeskAnalytics";

// WAYAK: «Verificar con Google» está APAGADO. El botón llama a
// POST /auth/link-google y espera que el backend haga la purga de credenciales
// y escriba la marca authIdentityProofs/{uid}; el /auth/link-google de WAYAK
// todavía no hace ninguna de las dos (eso llega con la unión de auth). Ofrecer
// el botón cerraría la sesión de la persona sin dejarla como staff. Mientras
// tanto el staff del agente IA se declara por SD_AI_STAFF_USER_IDS o por
// compañía + rol; la regla por dominio de correo falla cerrado.
const GOOGLE_IDENTITY_PROOF_AVAILABLE = false;

export default function TicketDetailSection() {
  const { t } = useTranslation("serviceDesk");
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user } = useUserStore();
  const { showAlert } = useAlert();
  const { isReseller } = useIsReseller();
  const { allCompanies, fetchAllCompanies } = useCompaniesStore();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [repositories, setRepositories] = useState<GithubRepository[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showGithubModal, setShowGithubModal] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState<{ value: string; label: string } | null>(null);
  // Staff del agente IA (aprobar / retirar / reintentar / reactivar). Veredicto
  // del backend (GET /serviceDesk/staff/me); mientras no llegue, o si falla, los
  // controles NO se pintan. El servidor sigue siendo la autoridad en cada acción.
  const [isAiStaff, setIsAiStaff] = useState(false);
  // Es del dominio de staff pero su cuenta aún no tiene la prueba de identidad
  // con Google: se le ofrece «Verificar con Google».
  const [needsGoogleProof, setNeedsGoogleProof] = useState(false);
  const [provingGoogle, setProvingGoogle] = useState(false);
  const [googleProofError, setGoogleProofError] = useState<string | null>(null);
  // Un 403 de agente (u otro fallo de carga) se explica en pantalla en vez de
  // decir "Ticket no encontrado.", que es lo contrario de lo que pasa.
  const { error: loadError, capture } = useDeskLoadError();

  // Carga de solo lectura: no mutar el ticket al abrirlo. La compañía se
  // asigna explícitamente desde el selector de gestión (patch), nunca en load.
  const load = useCallback(async () => {
    const data = await getTicket(id);
    if (!data) return;
    setTicket(data.ticket ?? null);
    setComments(data.comments || []);
    setAudit(data.audit || []);
  }, [id]);

  const showAuthModal = useCallback(async () => {
    const { value: formValues } = await Swal.fire({
      title: "Verificar cuenta",
      html: `
        <input id="swal-username" class="swal2-input" placeholder="Usuario o correo electrónico">
        <input id="swal-password" class="swal2-input" type="password" placeholder="Contraseña">
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Verificar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: themeBrand.primary,
      preConfirm: () => {
        const username = (document.getElementById('swal-username') as HTMLInputElement)?.value;
        const password = (document.getElementById('swal-password') as HTMLInputElement)?.value;
        if (!username || !password) {
          Swal.showValidationMessage('Por favor ingresa usuario y contraseña');
          return false;
        }
        return { username, password };
      }
    });

    if (formValues) {
      await authenticateUser(
        formValues.username,
        formValues.password,
        navigate,
        showAlert,
        t
      );
      // authenticateUser maneja sus errores internamente y no lanza excepciones:
      // verificar en el store si la sesión realmente se creó.
      if (useUserStore.getState().user) {
        // Si la autenticación fue exitosa, recargar el componente
        window.location.reload();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error de autenticación",
          text: "Usuario o contraseña incorrectos",
          confirmButtonColor: themeBrand.primary,
        });
        navigate(`${SERVICE_DESK_BASE}/tickets`);
      }
    } else {
      navigate(`${SERVICE_DESK_BASE}/tickets`);
    }
  }, [navigate, showAlert, t, isDarkMode, themeBrand.primary]);

  useEffect(() => {
    // Verificar autenticación antes de cargar el ticket
    if (!user) {
      setAuthChecked(true);
      showAuthModal();
      return;
    }

    setAuthChecked(true);
    // Cargar compañías si es reseller
    if (isReseller) {
      fetchAllCompanies();
    }
    // Los repositorios van aparte: son los CONFIGURADOS para la mesa y sin
    // GITHUB_TOKEN responden 501, lo que no debe tumbar la ficha entera.
    listGithubRepositories().then(setRepositories).catch(() => setRepositories([]));
    Promise.all([load(), getSettings().then(setSettings), getAssignableUsers().then(setUsers)])
      .catch(capture)
      .finally(() => setLoading(false));
  }, [id, load, user, showAuthModal, isReseller, fetchAllCompanies, capture]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getAiStaffStatusDetail()
      .then((v) => {
        if (cancelled) return;
        setIsAiStaff(v?.isAiStaff === true);
        setNeedsGoogleProof(GOOGLE_IDENTITY_PROOF_AVAILABLE && v?.needsGoogleProof === true);
      })
      .catch(() => !cancelled && setIsAiStaff(false));
    return () => {
      cancelled = true;
    };
  }, [user]);

  // `patch` es el unico camino por el que pasan TODOS los cambios de campo del
  // ticket (estado, prioridad, asignacion, escalamiento, etiquetas). Se
  // instrumenta aqui, comparando contra el `ticket` anterior que sigue en
  // estado, en vez de en los doce `onChange` de la UI.
  const patch = async (p: Parameters<typeof updateTicket>[1]) => {
    const before = ticket;
    const wasApproved = isAiAgentApproved(ticket?.aiAgent);
    setSaving(true);
    try {
      const updated = await updateTicket(id, p);
      if (before) {
        const priority = String(before.priority ?? "unknown");
        if (p.status && p.status !== before.status) {
          // `sla_state` es la propiedad cara: deja ver cuantos movimientos
          // ocurren cuando el SLA ya esta roto, o sea cuando el cliente ya se
          // enojo. Ni titulo, ni descripcion, ni referencia, ni solicitante.
          trackTicketStatusChanged({
            fromStatus: String(before.status ?? "unknown"),
            toStatus: String(p.status),
            priority,
            escalated: Boolean(before.escalated),
            slaState: String(before.slaStatus?.overall ?? "unknown"),
            method: "detail_select",
          });
        }
        if (p.priority && p.priority !== before.priority) {
          trackTicketPriorityChanged(priority, String(p.priority));
        }
        if ("assignedAgentId" in p && p.assignedAgentId !== before.assignedAgentId) {
          trackTicketAssigned({
            target: "agent",
            wasAssigned: Boolean(before.assignedAgentId),
            priority,
            escalated: Boolean(before.escalated),
          });
        }
        if ("assignedDevId" in p && p.assignedDevId !== before.assignedDevId) {
          trackTicketAssigned({
            target: "dev",
            wasAssigned: Boolean(before.assignedDevId),
            priority,
            escalated: Boolean(before.escalated),
          });
        }
        if ("escalated" in p && p.escalated !== before.escalated) {
          // Es el momento en que un ticket deja de ser soporte y pasa a costar
          // tiempo de ingenieria.
          trackTicketEscalationChanged({
            escalated: Boolean(p.escalated),
            priority,
            status: String(before.status ?? "unknown"),
          });
        }
      }
      setTicket(updated);
      await load();
      // Etiquetas/categoría/prioridad forman parte del contenido aprobado para
      // el agente IA: cambiarlas retira la aprobación (el backend lo audita).
      if (wasApproved && !isAiAgentApproved(updated?.aiAgent)) {
        Swal.fire({
          icon: "info",
          title: "Se retiró la aprobación del agente IA",
          text: "Cambiaste contenido que el staff había aprobado. Revísalo y vuelve a aprobar el ticket si el agente debe trabajarlo.",
        });
      }
    } catch (err: unknown) {
      if (p.status && before && p.status !== before.status) {
        trackTicketStatusChangeFailed({
          toStatus: String(p.status),
          method: "detail_select",
        });
      }
      // p. ej. 403: solo agentes de la mesa cambian etiquetas, categoría o
      // prioridad. Antes el error se relanzaba y quedaba como promesa sin
      // manejar: el agente no se enteraba de que el cambio no se guardó.
      Swal.fire({
        icon: "error",
        title: "No se pudo actualizar el ticket",
        text: apiErrorMessage(err, "Error al guardar el cambio."),
      });
    } finally {
      setSaving(false);
    }
  };

  const onReply = async (body: string, files: File[], internal: boolean) => {
    await addAgentComment(id, body, internal, files);
    // `internal` separa la nota interna de la respuesta al cliente. El cuerpo
    // del comentario y los nombres de los adjuntos NO viajan.
    trackTicketReplied({
      internal,
      fileCount: files.length,
      hasBody: body.trim().length > 0,
    });
    await load();
  };

  // El selector sólo ofrece los repositorios CONFIGURADOS para esta mesa
  // (Configuración → Repositorios de GitHub + el repo por defecto del
  // despliegue): el servidor rechaza con 422 cualquier otro. Si la lista está
  // vacía o no se pudo leer, se ofrece crear el issue en el repositorio por
  // defecto, que es el camino que el servidor acepta sin elegir nada.
  const onLinkGithub = async () => {
    if (repositories.length === 0) {
      const confirmed = await Swal.fire({
        title: t("ticketDetail.github.createAndLink"),
        icon: "question",
        text:
          "No hay repositorios configurados para la mesa (o no se pudo leer la lista). ¿Crear el issue en el repositorio por defecto? Para elegir otro, añádelo en Configuración → Repositorios de GitHub.",
        showCancelButton: true,
        confirmButtonText: "Usar el repositorio por defecto",
        confirmButtonColor: themeBrand.primary,
      });
      if (!confirmed.isConfirmed) return;
      await createGithubIssue("");
      return;
    }
    setShowGithubModal(true);
    setSelectedRepo(null);
  };

  const handleGithubConfirm = async () => {
    if (!selectedRepo) return;
    setShowGithubModal(false);
    await createGithubIssue(selectedRepo.value);
  };

  const createGithubIssue = async (repo: string) => {
    try {
      const updated = await linkGithubIssue(id, repo ? { repo } : {});
      // Sin el nombre del repositorio ni la URL: solo si se creo o se enlazo.
      trackTicketGithubLinked({ mode: "create", success: true });
      setTicket(updated);
      await load();
      Swal.fire({ icon: "success", title: t("ticketDetail.github.success"), confirmButtonColor: themeBrand.primary });
    } catch (err: unknown) {
      trackTicketGithubLinked({ mode: "create", success: false });
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      Swal.fire({ icon: "error", title: t("ticketDetail.github.error"), text: typeof msg === "string" ? msg : "" });
    }
  };

  const onLinkExistingGithub = async () => {
    const { value: url } = await Swal.fire({
      title: t("ticketDetail.github.linkModalTitle"),
      input: "url",
      inputLabel: t("ticketDetail.github.linkModalLabel"),
      inputPlaceholder: t("ticketDetail.github.linkModalPlaceholder"),
      showCancelButton: true,
      confirmButtonText: t("ticketDetail.github.linkModalConfirm"),
      confirmButtonColor: themeBrand.primary,
      inputValidator: (v) => (!v ? t("ticketDetail.github.linkModalValidator") : undefined),
    });
    if (!url) return;
    try {
      const updated = await linkExistingGithubIssue(id, url);
      trackTicketGithubLinked({ mode: "link_existing", success: true });
      setTicket(updated);
      await load();
      Swal.fire({ icon: "success", title: t("ticketDetail.github.linkSuccess"), confirmButtonColor: themeBrand.primary });
    } catch (err: unknown) {
      trackTicketGithubLinked({ mode: "link_existing", success: false });
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      Swal.fire({ icon: "error", title: t("ticketDetail.github.linkError"), text: typeof msg === "string" ? msg : "" });
    }
  };

  const onDeleteTicket = async () => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar ticket?",
      html: `Se eliminará permanentemente el ticket <strong>${ticket?.reference}</strong>. Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      confirmButtonColor: DIALOG_COLORS.danger,
      cancelButtonText: "Cancelar",
    });

    if (!isConfirmed) return;

    try {
      await deleteTicket(id);
      trackTicketDeleted({
        status: String(ticket?.status ?? "unknown"),
        priority: String(ticket?.priority ?? "unknown"),
        escalated: Boolean(ticket?.escalated),
      });
      await Swal.fire({
        icon: "success",
        title: "Ticket eliminado",
        text: "El ticket ha sido eliminado correctamente",
        confirmButtonColor: themeBrand.primary,
      });
      navigate(`${SERVICE_DESK_BASE}/tickets`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { meta?: { message?: string } } } })?.response?.data?.meta?.message;
      Swal.fire({
        icon: "error",
        title: "Error al eliminar",
        text: typeof msg === "string" ? msg : "No se pudo eliminar el ticket",
        confirmButtonColor: themeBrand.primary,
      });
    }
  };

  // Controles humanos del agente IA (solo staff; el backend responde 403 al
  // resto). La acción y la recarga se manejan por separado: si la acción ya se
  // aplicó, un fallo al recargar no debe decir que la acción falló.
  const runAiAction = async (action: () => Promise<unknown>, errorTitle: string) => {
    try {
      await action();
    } catch (err: unknown) {
      Swal.fire({ icon: "error", title: errorTitle, text: apiErrorMessage(err) });
      return;
    }
    try {
      await load();
    } catch {
      Swal.fire({ icon: "warning", title: "Hecho, pero no se pudo recargar el ticket", text: "Actualiza la página." });
    }
  };

  // Devuelve el ticket a la cola del agente IA. Útil cuando un intento falló,
  // cuando se cerró su PR sin merge o para reintentar con más contexto.
  const onRetryAi = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: "¿Reintentar con el agente IA?",
      text: "El ticket vuelve a “Abierto”, se reinician sus intentos y el agente lo tomará en su próxima corrida. Si tenía un PR abierto, ciérralo para no duplicar trabajo.",
      showCancelButton: true,
      confirmButtonText: "Reintentar",
      confirmButtonColor: themeBrand.primary,
    });
    if (!isConfirmed) return;
    await runAiAction(() => releaseAiAgent(id), "No se pudo reencolar");
  };

  const onApproveAi = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "¿Aprobar para el agente IA?",
      text: "El agente podrá leer este ticket, cambiar código en el repositorio y abrir un Pull Request. Aprueba solo reportes que revisaste y que no contienen instrucciones sospechosas.",
      showCancelButton: true,
      confirmButtonText: "Aprobar",
      confirmButtonColor: themeBrand.primary,
    });
    if (!isConfirmed) return;
    await runAiAction(() => approveAiAgent(id), "No se pudo aprobar");
  };

  const onRevokeAi = () => runAiAction(() => revokeAiAgentApproval(id), "No se pudo retirar la aprobación");

  // Puente a ingeniería (#510): al aprobar, el backend crea (o reutiliza) el
  // issue de GitHub y encola el trabajo del agente. Si quedó pendiente (sin
  // GITHUB_TOKEN, varios repos) o falló, esto lo reintenta. El repo sólo se
  // pregunta cuando el backend no pudo resolverlo, y sólo entre los de la mesa.
  const onRetryBridge = async () => {
    let repo: string | undefined;
    if (engineeringBridgeNeedsRepo(ticket?.engineeringBridge)) {
      let repos: GithubRepository[] = [];
      try {
        repos = await listGithubRepositories();
      } catch {
        repos = [];
      }
      if (!repos.length) {
        Swal.fire({
          icon: "info",
          title: "Sin repositorios",
          text: "Añade los repositorios de la mesa en Ajustes → Repositorios de GitHub y vuelve a intentarlo.",
        });
        return;
      }
      const picked = await Swal.fire({
        title: "Llevar a ingeniería",
        input: "select",
        inputOptions: Object.fromEntries(repos.map((r) => [r.fullName, r.fullName])),
        inputLabel: "Repositorio de la mesa",
        inputValue: repos[0].fullName,
        showCancelButton: true,
        confirmButtonText: "Crear issue",
        confirmButtonColor: themeBrand.primary,
      });
      if (!picked.isConfirmed) return;
      repo = String(picked.value ?? "") || undefined;
    }
    await runAiAction(() => retryEngineeringBridge(id, repo), "No se pudo llevar a ingeniería");
  };

  // Etapa del ciclo de desarrollo (#559). La mayoría se mueve sola con eventos
  // reales; esto es la corrección humana y la única vía práctica para «Validado
  // por staff» y «Rechazado en QA». El cliente ve el resultado, así que se pide
  // confirmación con una nota opcional que sólo queda en la vista interna.
  const onChangeStage = async (stage: DevStageId) => {
    if (!ticket || stage === ticket.devStage?.current) return;
    const { isConfirmed, value: note } = await Swal.fire({
      icon: "question",
      title: "¿Mover la etapa del ticket?",
      text: "El cliente verá la etapa nueva y su fecha (nunca esta nota ni los enlaces técnicos).",
      input: "text",
      inputPlaceholder: "Nota interna (opcional)",
      showCancelButton: true,
      confirmButtonText: "Mover etapa",
      confirmButtonColor: themeBrand.primary,
    });
    if (!isConfirmed) return;
    setSaving(true);
    try {
      await setTicketStage(id, stage, note ? { note } : {});
      await load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo mover la etapa", text: apiErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const card = "bg-surface border-line";
  const text = "text-content";
  const muted = "text-content-muted";
  const selCls = `w-full rounded-lg border px-3 py-1.5 text-sm !bg-app !border-line !text-content`;

  if (!authChecked) return <div className="p-8 text-center text-gray-400">Verificando autenticación...</div>;
  if (loading) return <div className="p-8 text-center text-gray-400">{t("ticketDetail.loading")}</div>;
  if (loadError) return <DeskLoadErrorPanel error={loadError} />;
  if (!ticket || !settings) return <div className="p-8 text-center text-gray-400">{t("ticketDetail.notFound")}</div>;

  const toggleLabel = (labelId: string) => {
    const set = new Set(ticket.labels || []);
    if (set.has(labelId)) set.delete(labelId); else set.add(labelId);
    patch({ labels: Array.from(set) });
  };

  const internalCount = comments.filter((c) => c.internal).length;

  // Requester-driven closure (CC-CONSOLE #1337): who may move THIS ticket to
  // "Cerrado". Computed with the backend's rule to disable the option and say
  // why; if it is attempted anyway, the 403 carries its own message.
  const closePermission = canCloseTicket(ticket, { id: user?.id, email: user?.email }, Date.now());
  const isClosed = ticket.status === CLOSED_STATUS;
  const closureHint = isClosed
    ? null
    : closePermission.reason === "locked" && closePermission.unlockAt == null
      ? t("closure.hint.lockedNoDate", { days: ticket.closure?.lockDays ?? 7 })
      : t(`closure.hint.${closePermission.reason}`, {
          name: ticket.requester?.name || ticket.requester?.email || t("closure.hint.requesterFallback"),
          date: formatClosureDate(closePermission.unlockAt),
          days: ticket.closure?.lockDays ?? 7,
        });
  const rework = ticket.rework && ticket.rework.count > 0 ? ticket.rework : null;
  // `patch` rethrows; a rejected status move (transition not allowed, a close
  // that is not theirs) used to vanish as an unhandled promise.
  const changeStatus = (status: string) =>
    patch({ status }).catch((err: unknown) =>
      showAlert({ icon: "error", title: t("closure.moveError"), text: apiErrorMessage(err) }),
    );

  return (
    <div className="p-6">
      <button onClick={() => navigate(`${SERVICE_DESK_BASE}/tickets`)} className={`mb-4 text-sm ${muted} hover:underline`}>← {t("ticketsList.title")}</button>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Main */}
        <div className="space-y-5 lg:col-span-2">
          <div className={`rounded-xl border p-5 ${card}`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-gray-400">{ticket.reference}</span>
              <PriorityBadge priority={ticket.priority} />
              <SlaBadge state={ticket.slaStatus?.overall} />
              <AiAgentBadge state={ticket.aiAgent?.state} />
              <StageBadge devStage={ticket.devStage} />
              <ReworkChip count={ticket.rework?.count} />
              <ChannelChip channel={ticket.channel} />
              <SampleChip ticket={ticket} />
              {ticket.slaStatus?.paused && <span className="text-xs text-amber-600">SLA en pausa</span>}
              {ticket.escalated && <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-600">Escalado</span>}
            </div>
            <h1 className={`text-xl font-bold ${text}`}>{ticket.title}</h1>
            <p className={`mt-1 text-xs ${muted}`}>
              {ticket.brandLabel} · {ticket.requester?.name || "Anónimo"} ({ticket.requester?.email || "sin correo"}) · {formatDate(ticket.createdAt)}
            </p>
            {isReseller && (
              <div className="mt-2">
                <label className={`block text-xs font-medium ${muted} mb-1`}>Compañía</label>
                <select
                  className={selCls}
                  value={ticket.companyId || ""}
                  onChange={(e) => e.target.value && patch({ companyId: e.target.value })}
                  disabled={saving}
                >
                  {/* La compañía es obligatoria: se puede cambiar, no vaciar. La
                      opción vacía sólo aparece en tickets anteriores a la regla. */}
                  {!ticket.companyId && <option value="">Sin compañía (ticket antiguo)</option>}
                  {allCompanies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || c.business_name}</option>
                  ))}
                </select>
              </div>
            )}
            {!isReseller && ticket.companyId && (
              <p className={`mt-2 text-xs ${muted}`}>
                Compañía: {allCompanies.find(c => c.id === ticket.companyId)?.name || ticket.companyName || ticket.companyId}
              </p>
            )}
            {ticket.description && <p className={`mt-4 whitespace-pre-wrap text-sm ${text}`}>{ticket.description}</p>}
          </div>

          {/* Public thread — only customer-facing comments; replies notify the client. */}
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.comments.title")}</h2>
            <CommentThread
              comments={comments}
              onReply={onReply}
              replyLabel={t("ticketDetail.comments.replyPlaceholder")}
              emptyLabel={t("ticketDetail.comments.noComments")}
            />
          </div>

          {/* Internal team notes — invisible to the customer, never emailed (#34). */}
          <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-5 dark:bg-amber-500/10">
            <div className="mb-1 flex items-center gap-2">
              <h2 className="text-sm font-semibold text-amber-800 dark:text-amber-300">{t("ticketDetail.comments.internal")}</h2>
              {internalCount > 0 && (
                <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-800">{internalCount}</span>
              )}
            </div>
            <p className="mb-3 text-xs text-amber-700 dark:text-amber-300/80">
              {t("ticketDetail.comments.internalToggle")}
            </p>
            <CommentThread
              comments={comments}
              onReply={onReply}
              internalOnly
              replyLabel={t("ticketDetail.comments.replyPlaceholder")}
            />
          </div>

          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.auditTrail")}</h2>
            {audit.length === 0 ? (
              <p className="text-sm text-gray-400">{t("ticketDetail.noAuditEvents")}</p>
            ) : (
              <ul className="space-y-2">
                {audit.map((a) => {
                  const actionKey = `audit.actions.${a.action}`;
                  const actionLabel = t(actionKey) !== actionKey ? t(actionKey) : a.action;
                  const formatValue = (field: string | undefined, value: unknown): string => {
                    if (!value || value === "—") return "—";
                    const strVal = String(value);
                    if (field === "priority") {
                      const priorityMap: Record<string, string> = {
                        low: t("ticketDetail.management.priorities.low"),
                        medium: t("ticketDetail.management.priorities.medium"),
                        high: t("ticketDetail.management.priorities.high"),
                        urgent: t("ticketDetail.management.priorities.urgent"),
                      };
                      return priorityMap[strVal] || strVal;
                    }
                    if (field === "assignedAgentId") {
                      const agent = users.find(u => u.id === strVal);
                      if (agent) return agent.name;
                      return strVal === ticket.assignedAgentId && ticket.assignedAgentName ? ticket.assignedAgentName : strVal;
                    }
                    if (field === "status") {
                      return settings?.statuses?.find((s) => s.id === strVal)?.label || strVal;
                    }
                    if (field === "devStage.current") {
                      return stageLabelOf(settings, strVal as DevStageId);
                    }
                    return strVal;
                  };
                  return (
                    <li key={a.id} className={`flex items-start justify-between text-xs ${muted}`}>
                      <span>
                        <strong>{a.actorName || t("audit.system")}</strong> — {actionLabel}
                        {a.field && a.to !== undefined ? `: ${formatValue(a.field, a.from)} → ${formatValue(a.field, a.to)}` : ""}
                      </span>
                      <span className="ml-3 whitespace-nowrap">{formatDate(a.createdAt)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Management sidebar */}
        <div className="space-y-4">
          {/* Ciclo de desarrollo (#559) — VISTA INTERNA: línea de tiempo con
              enlaces a issue / PR / QA y la bitácora de quién movió qué. La
              vista del cliente sólo recibe etapa y fechas. */}
          <div className={`rounded-xl border p-5 ${card}`}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className={`text-sm font-semibold ${text}`}>Ciclo de desarrollo</h2>
              <StageBadge devStage={ticket.devStage} />
            </div>

            {ticket.devStage?.timeline?.length ? (
              <StageTimeline
                steps={ticket.devStage.timeline}
                currentLabel={ticket.devStage.label}
                compact
                isDarkMode={isDarkMode}
              />
            ) : (
              <p className={`text-xs ${muted}`}>Sin avances registrados todavía.</p>
            )}

            <FieldRow label="Mover a" muted={muted}>
              <select
                className={selCls}
                value={ticket.devStage?.current || "received"}
                onChange={(e) => onChangeStage(e.target.value as DevStageId)}
                disabled={saving}
              >
                {selectableStages(settings).map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </FieldRow>
            <p className={`-mt-2 mb-3 text-[11px] ${muted}`}>
              Las etapas avanzan solas con los eventos reales (issue vinculado, rama, PR, deploy a QA). Aquí se
              corrigen a mano y se marcan «Validado por staff» y «Rechazado en QA».
            </p>

            {hasStageLinks(ticket.devStage?.links) && (
              <div className={`border-t pt-3 border-line`}>
                <span className={`mb-1.5 block text-xs font-medium ${muted}`}>Enlaces técnicos (sólo internos)</span>
                <div className="flex flex-col gap-1.5">
                  {ticket.devStage?.links?.issueUrl && (
                    <a href={ticket.devStage.links.issueUrl} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-500 underline">
                      Issue del desarrollo →
                    </a>
                  )}
                  {ticket.devStage?.links?.prUrl && (
                    <a href={ticket.devStage.links.prUrl} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-500 underline">
                      Pull Request →
                    </a>
                  )}
                  {ticket.devStage?.links?.qaUrl && (
                    <a href={ticket.devStage.links.qaUrl} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-500 underline">
                      Ambiente de QA →
                    </a>
                  )}
                  {ticket.devStage?.links?.branch && (
                    <span className={`truncate font-mono text-[11px] ${muted}`} title={ticket.devStage.links.branch}>
                      Rama: {ticket.devStage.links.branch}
                    </span>
                  )}
                  {ticket.devStage?.links?.repo && (
                    <span className={`font-mono text-[11px] ${muted}`}>Repo: {ticket.devStage.links.repo}</span>
                  )}
                </div>
              </div>
            )}

            {Boolean(ticket.devStage?.history?.length) && (
              <details className={`mt-3 border-t pt-3 border-line`}>
                <summary className={`cursor-pointer text-xs font-medium ${muted}`}>
                  Bitácora de etapas ({ticket.devStage?.history?.length})
                </summary>
                <ul className="mt-2 space-y-1.5">
                  {[...(ticket.devStage?.history || [])].reverse().map((h, i) => (
                    <li key={`${h.stage}-${h.at ?? i}`} className={`text-[11px] ${muted}`}>
                      <strong className={text}>{h.label}</strong> · {h.actorName || SOURCE_LABEL[h.source] || "Sistema"} ·{" "}
                      {formatDate(h.at ?? undefined)}
                      {h.note ? <span className="block opacity-80">{h.note}</span> : null}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.management.title")}</h2>
            <FieldRow label={t("ticketDetail.management.status")} muted={muted}>
              <select className={selCls} value={ticket.status} onChange={(e) => changeStatus(e.target.value)} disabled={saving}>
                {(settings.statuses || [])
                  .filter((s) => allowedNextStatuses(settings, ticket.status).includes(s.id))
                  .map((s) => (
                    <option key={s.id} value={s.id} disabled={s.id === CLOSED_STATUS && !isClosed && !closePermission.allowed}>
                      {s.label}
                    </option>
                  ))}
              </select>
              {closureHint && (
                <p className={`mt-1 text-xs ${closePermission.allowed ? muted : "text-amber-600"}`}>{closureHint}</p>
              )}
            </FieldRow>
            <FieldRow label={t("ticketDetail.management.priority")} muted={muted}>
              <select className={selCls} value={ticket.priority} onChange={(e) => patch({ priority: e.target.value as TicketPriority })} disabled={saving}>
                <option value="low">{t("ticketDetail.management.priorities.low")}</option>
                <option value="medium">{t("ticketDetail.management.priorities.medium")}</option>
                <option value="high">{t("ticketDetail.management.priorities.high")}</option>
                <option value="urgent">{t("ticketDetail.management.priorities.urgent")}</option>
              </select>
            </FieldRow>
            <FieldRow label={t("ticketDetail.management.brand")} muted={muted}>
              <select className={selCls} value={ticket.brand} onChange={(e) => patch({ brand: e.target.value })} disabled={saving}>
                {(settings.brands || []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </FieldRow>
            <FieldRow label={t("ticketDetail.management.category")} muted={muted}>
              <select className={selCls} value={ticket.category} onChange={(e) => patch({ category: e.target.value })} disabled={saving}>
                {(settings.categories || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </FieldRow>
            <FieldRow label="Módulo afectado" muted={muted}>
              <select className={selCls} value={ticket.module || ""} onChange={(e) => patch({ module: e.target.value || "" })} disabled={saving}>
                <option value="">Sin módulo</option>
                {MODULE_OPTIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </FieldRow>
            {ticket.module && (MODULE_OPTIONS.find(m => m.id === ticket.module)?.sections?.length ?? 0) > 0 && (
              <FieldRow label="Sección" muted={muted}>
                <select className={selCls} value={ticket.moduleSection || ""} onChange={(e) => patch({ moduleSection: e.target.value || "" })} disabled={saving}>
                  <option value="">Sin sección</option>
                  {MODULE_OPTIONS.find(m => m.id === ticket.module)?.sections.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </FieldRow>
            )}
            <FieldRow label={t("ticketDetail.management.assignedAgent")} muted={muted}>
              <AgentSelector
                value={ticket.assignedAgentId}
                onChange={(agentId) => patch({ assignedAgentId: agentId })}
                isDarkMode={isDarkMode}
                users={users}
                placeholder={t("ticketDetail.management.notAssigned")}
                isDisabled={saving}
                fallbackName={ticket.assignedAgentName}
              />
            </FieldRow>
          </div>

          {/* Closure and times (CC-CONSOLE #1337): what is NOT the team's time —
              waiting on the customer and how long the requester takes to
              confirm — and how many times it came back from "Resuelto". */}
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("closure.panel.title")}</h2>
            <dl className={`space-y-1.5 text-xs ${muted}`}>
              <div className="flex justify-between gap-2">
                <dt>{t("closure.panel.waitingCustomer")}</dt>
                <dd className={text}>{formatDurationMs(ticket.slaStatus?.waitingCustomerMs)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt>{t("closure.panel.confirmation")}</dt>
                <dd className={`text-right ${text}`}>
                  {formatDurationMs(ticket.slaStatus?.closureMs)}
                  {ticket.closure?.pending && ticket.slaStatus?.closureMs != null ? ` (${t("closure.panel.running")})` : ""}
                </dd>
              </div>
              {ticket.closure?.pending && (
                <div className="flex justify-between gap-2">
                  <dt>{t("closure.panel.resolvedAt")}</dt>
                  <dd className={text}>{formatDate(ticket.closure.resolvedAt)}</dd>
                </div>
              )}
              {ticket.closedBy && (
                <div className="flex justify-between gap-2">
                  <dt>{t("closure.panel.closedBy")}</dt>
                  <dd className={`text-right ${text}`}>
                    {ticket.closedBy.name || "—"} ({t(`closure.panel.roles.${ticket.closedBy.role}`)})
                  </dd>
                </div>
              )}
              <div className="flex justify-between gap-2">
                <dt>{t("closure.panel.reworkCount")}</dt>
                <dd className={text}>{ticket.rework?.count ?? 0}</dd>
              </div>
            </dl>
            {rework && (rework.lastReason || rework.lastByName) && (
              <div className="mt-3 rounded-lg bg-orange-50 p-3 text-xs text-orange-800 dark:bg-orange-500/10 dark:text-orange-200">
                <p className="font-semibold">
                  {t("closure.panel.lastRework")}
                  {rework.lastByName ? ` · ${rework.lastByName}` : ""}
                  {rework.lastByRole ? ` (${t(`closure.panel.reworkRoles.${rework.lastByRole}`)})` : ""}
                  {rework.lastAt ? ` · ${formatDate(rework.lastAt)}` : ""}
                </p>
                {rework.lastReason && <p className="mt-1 whitespace-pre-wrap">{rework.lastReason}</p>}
              </div>
            )}
          </div>

          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>Etiquetas</h2>
            <div className="flex flex-wrap gap-2">
              {(settings.labels || []).map((l) => {
                const active = (ticket.labels || []).includes(l.id);
                return (
                  <button
                    key={l.id}
                    onClick={() => toggleLabel(l.id)}
                    disabled={saving}
                    className="rounded-full px-2.5 py-1 text-xs font-medium"
                    style={{ color: active ? "#fff" : l.color, backgroundColor: active ? l.color : `${l.color}1a` }}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.management.escalation.title")}</h2>
            <label className={`flex items-center gap-2 text-sm ${text}`}>
              <input type="checkbox" checked={Boolean(ticket.escalated)} onChange={(e) => patch({ escalated: e.target.checked })} disabled={saving} />
              {t("ticketDetail.management.escalation.label")}
            </label>
            {ticket.escalated && (
              <FieldRow label={t("ticketDetail.management.escalation.assignedDev")} muted={muted}>
                <AgentSelector
                  value={ticket.assignedDevId}
                  onChange={(agentId) => patch({ assignedDevId: agentId })}
                  isDarkMode={isDarkMode}
                  users={users}
                  placeholder={t("ticketDetail.management.notAssigned")}
                  isDisabled={saving}
                />
              </FieldRow>
            )}
          </div>

          {/* Respuesta inmediata con IA (#508) — la escribe el trigger de
              creación del ticket. Es informativa: el staff ve QUÉ se le dijo al
              cliente y el triage que hizo el modelo. La respuesta en sí vive en
              el hilo público, marcada como automática. */}
          <AiFirstReplyPanel
            ticket={ticket}
            settings={settings}
            card={card}
            text={text}
            muted={muted}
            saving={saving}
            onToggleDisabled={(next) => patch({ aiFirstReplyDisabled: next })}
          />

          {/* Conversación continua con IA (#509) — la escribe el trigger de
              comentarios. Muestra turnos, costo y, si hubo traspaso, por qué;
              desde aquí un agente puede reactivarla. */}
          <AiConversationPanel
            ticket={ticket}
            settings={settings}
            card={card}
            text={text}
            muted={muted}
            saving={saving}
            onResume={isAiStaff ? () => patch({ aiConversationResume: true }) : undefined}
          />

          {/* Agente IA — el estado lo escribe el runner local vía /serviceDesk/ai/*.
              Aquí el staff aprueba el ticket para el agente (puerta de
              confianza), retira la aprobación o lo reintenta. */}
          {/* Un ticket de ejemplo del Sandbox nunca va al agente de código. */}
          {!isSampleTicket(ticket) && (settings.aiAgent?.enabled || (ticket.aiAgent && ticket.aiAgent.state !== "idle")) && (
            <div className={`rounded-xl border p-5 ${card}`}>
              <div className="mb-3 flex items-center justify-between">
                <h2 className={`text-sm font-semibold ${text}`}>Agente IA</h2>
                <AiAgentBadge state={ticket.aiAgent?.state} />
              </div>

              {ticket.aiAgent && isAiAgentApproved(ticket.aiAgent) ? (
                <div className="mb-3 rounded bg-emerald-50 p-2 text-xs text-emerald-700 dark:bg-emerald-500/10">
                  <div className="flex items-center justify-between gap-2">
                    <span>
                      Aprobado para el agente por {ticket.aiAgent.approvedByName || "staff"} · {formatDate(ticket.aiAgent.approvedAt)}
                    </span>
                    {isAiStaff && (
                      <button onClick={onRevokeAi} className="font-semibold underline">Retirar</button>
                    )}
                  </div>
                  <p className="mt-1 opacity-80">
                    El agente solo recibe el contenido aprobado y las notas internas del staff. Cambiar etiquetas, categoría o prioridad retira la aprobación.
                  </p>
                </div>
              ) : aiAgentNeedsApproval(ticket, settings) ? (
                <div className="mb-3 space-y-2">
                  <p className={`text-xs ${muted}`}>
                    {ticket.source === "authenticated"
                      ? "Pendiente de aprobación: el agente no toma tickets hasta que alguien del staff los aprueba."
                      : "Ticket del portal público: siempre requiere aprobación del staff antes de que el agente lo tome."}
                  </p>
                  {isAiStaff ? (
                    <button
                      onClick={onApproveAi}
                      className="w-full rounded-lg px-4 py-2 text-sm font-semibold text-white"
                      style={{ backgroundColor: themeBrand.primary }}
                    >
                      Aprobar para el agente IA
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <p className={`text-[11px] ${muted}`} data-testid="ai-approve-staff-only">
                        Solo el staff del agente IA puede aprobarlo.
                        {GOOGLE_IDENTITY_PROOF_AVAILABLE &&
                          " Si eres staff por dominio, verifica tu identidad con Google una vez con tu cuenta del dominio: se cierran todas tus sesiones y tokens anteriores y vuelves a entrar."}
                      </p>
                      {needsGoogleProof && (
                        <button
                          type="button"
                          data-testid="ai-verify-google"
                          disabled={provingGoogle}
                          onClick={async () => {
                            setGoogleProofError(null);
                            setProvingGoogle(true);
                            try {
                              // Import perezoso: arrastra Firebase Auth y el logout.
                              const { proveIdentityWithGoogle } = await import("../services/googleIdentityProof");
                              await proveIdentityWithGoogle();
                            } catch {
                              setGoogleProofError("No se pudo verificar tu identidad con Google. Inténtalo de nuevo.");
                            } finally {
                              setProvingGoogle(false);
                            }
                          }}
                          className="w-full rounded-lg border px-4 py-2 text-sm font-semibold"
                        >
                          {provingGoogle ? "Verificando…" : "Verificar con Google"}
                        </button>
                      )}
                      {googleProofError && (
                        <p className="text-[11px] text-red-600" role="alert">{googleProofError}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : null}

              {ticket.engineeringBridge && (
                <div className="mb-3 rounded border p-2 text-xs" data-testid="engineering-bridge">
                  <div className="flex items-center justify-between gap-2">
                    <span className={text}>Puente a ingeniería</span>
                    <span
                      className="rounded px-2 py-0.5 font-semibold"
                      style={{
                        color: ENGINEERING_BRIDGE_META[ticket.engineeringBridge.state]?.color,
                        backgroundColor: ENGINEERING_BRIDGE_META[ticket.engineeringBridge.state]?.bg,
                      }}
                    >
                      {ENGINEERING_BRIDGE_META[ticket.engineeringBridge.state]?.label ?? ticket.engineeringBridge.state}
                    </span>
                  </div>
                  {ticket.engineeringBridge.message && (
                    <p className={`mt-1 ${muted}`}>{ticket.engineeringBridge.message}</p>
                  )}
                  {isAiStaff && canRetryEngineeringBridge(ticket.engineeringBridge) && (
                    <button onClick={onRetryBridge} className="mt-2 font-semibold underline">
                      Reintentar puente
                    </button>
                  )}
                </div>
              )}

              {ticket.aiAgent && (<>
              {ticket.aiAgent.planSummary && (
                <p className={`mb-3 whitespace-pre-wrap text-xs ${text}`}>{ticket.aiAgent.planSummary}</p>
              )}

              <dl className={`space-y-1 text-xs ${muted}`}>
                {ticket.aiAgent.repo && (
                  <div className="flex justify-between gap-2">
                    <dt>Repositorio</dt>
                    <dd className={`font-mono ${text}`}>{ticket.aiAgent.repo}</dd>
                  </div>
                )}
                {ticket.aiAgent.branch && (
                  <div className="flex justify-between gap-2">
                    <dt>Rama</dt>
                    <dd className={`truncate font-mono ${text}`} title={ticket.aiAgent.branch}>{ticket.aiAgent.branch}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <dt>Intentos</dt>
                  <dd className={text}>{ticket.aiAgent.attempts ?? 0}</dd>
                </div>
                {ticket.aiAgent.workedAt && (
                  <div className="flex justify-between gap-2">
                    <dt>Última corrida</dt>
                    <dd className={text}>{formatDate(ticket.aiAgent.workedAt)}</dd>
                  </div>
                )}
              </dl>

              {ticket.aiAgent.lastError && (
                <p className="mt-3 rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-500/10">
                  {ticket.aiAgent.lastError}
                </p>
              )}

              <div className="mt-3 flex flex-col gap-2">
                {ticket.aiAgent.prUrl && (
                  <a href={ticket.aiAgent.prUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-500 underline">
                    Ver Pull Request →
                  </a>
                )}
                {ticket.aiAgent.issueUrl && !ticket.githubIssue?.htmlUrl && (
                  <a href={ticket.aiAgent.issueUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-500 underline">
                    Ver issue →
                  </a>
                )}
                {/* claimed/planned solo con el lease vencido (corrida interrumpida);
                    pr-open = PR cerrado sin merge. Con el lease vivo el backend
                    responde 409: el botón nunca duplica a un runner activo. */}
                {isAiStaff && canRetryAiAgent(ticket.aiAgent) && (
                  <button onClick={onRetryAi} className={`rounded-lg border px-4 py-2 text-sm font-semibold ${card} ${text}`}>
                    Reintentar con el agente IA
                  </button>
                )}
              </div>

              <p className={`mt-3 text-[11px] ${muted}`}>
                {AI_AGENT_META[ticket.aiAgent.state]?.label}. El agente nunca hace merge: el PR
                espera revisión humana.
              </p>
              </>)}
            </div>
          )}

          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("ticketDetail.github.title")}</h2>
            {ticket.githubIssue?.htmlUrl ? (
              <div className="space-y-3">
                <a href={ticket.githubIssue.htmlUrl} target="_blank" rel="noreferrer" className={`block text-sm font-medium text-info underline`}>
                  #{ticket.githubIssue.number} {ticket.githubIssue.repo && `· ${ticket.githubIssue.repo}`}
                </a>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${
                    ticket.githubIssue.state === 'closed'
                      ? "bg-purple-500/15 text-purple-500"
                      : "bg-success/15 text-success"
                  }`}>
                    {ticket.githubIssue.state === 'closed' ? '✓ Cerrada' : '◯ Abierta'}
                  </span>
                </div>
                <button onClick={onLinkExistingGithub} className={`text-xs ${muted} hover:underline`}>
                  {t("ticketDetail.github.linkExisting")}
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <button onClick={onLinkGithub} className="rounded-lg px-4 py-2 text-sm font-semibold text-white hover:opacity-90" style={{ backgroundColor: themeBrand.primary }}>
                  {t("ticketDetail.github.createAndLink")}
                </button>
                <button onClick={onLinkExistingGithub} className={`rounded-lg border px-4 py-2 text-sm font-semibold border-line text-content-secondary hover:bg-subtle`}>
                  {t("ticketDetail.github.linkExisting")}
                </button>
              </div>
            )}
          </div>

          {isReseller && (
            <div className={`rounded-xl border p-5 ${card}`}>
              <h2 className={`mb-3 text-sm font-semibold ${text}`}>Acciones</h2>
              <button
                onClick={onDeleteTicket}
                className="w-full rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-900/50"
              >
                Eliminar ticket
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal selector de repositorio GitHub */}
      {showGithubModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`rounded-lg p-6 shadow-lg bg-surface w-96`} style={{ maxHeight: '85vh', overflow: 'visible' }}>
            <h2 className={`mb-4 text-lg font-bold ${text}`}>Selecciona un repositorio</h2>
            <div style={{ position: 'relative', zIndex: 50 }}>
              <Combobox
                options={repositories.map(r => ({
                  value: r.fullName,
                  label: `${r.fullName}${r.isPrivate ? ' (privado)' : ''}`,
                }))}
                value={selectedRepo}
                onChange={setSelectedRepo}
                placeholder="Busca un repositorio..."
                isClearable
                isSearchable
                maxMenuHeight={300}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                menuPortalTarget={document.body}
              />
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleGithubConfirm}
                disabled={!selectedRepo}
                className="flex-1 rounded-lg px-4 py-2 font-semibold text-white hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: themeBrand.primary }}
              >
                Crear issue
              </button>
              <button
                onClick={() => setShowGithubModal(false)}
                className={`flex-1 rounded-lg border px-4 py-2 font-semibold border-line text-content-secondary hover:bg-subtle`}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Quién movió la etapa cuando no hay nombre de persona.
const SOURCE_LABEL: Record<string, string> = {
  system: "Automático",
  agent: "Agente IA",
  staff: "Staff",
};

function FieldRow({ label, muted, children }: { label: string; muted: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <span className={`mb-1 block text-xs ${muted}`}>{label}</span>
      {children}
    </div>
  );
}

/**
 * Respuesta inmediata con IA (#508) — panel de SÓLO LECTURA.
 * El texto que el cliente recibió vive en el hilo público (marcado como
 * automático); aquí el staff ve si salió, cuándo y qué opinó el modelo del
 * ticket. No hay botón de "responder otra vez": la respuesta inmediata es
 * única por ticket, y a partir de ahí contestan personas.
 *
 * Se oculta si la mesa nunca la usó en este ticket y la tiene apagada, para no
 * llenar el escritorio de tarjetas vacías.
 */
export function AiFirstReplyPanel({
  ticket, settings, card, text, muted, saving = false, onToggleDisabled,
}: {
  ticket: Ticket;
  settings: ServiceDeskSettings;
  card: string;
  text: string;
  muted: string;
  saving?: boolean;
  onToggleDisabled?: (next: boolean) => void;
}) {
  const state: AiFirstReplyState = ticket.aiAgent?.firstReplyState || "idle";
  const enabled = settings.aiFirstReply?.enabled === true;
  if (!enabled && state === "idle") return null;

  const meta = AI_FIRST_REPLY_META[state] || AI_FIRST_REPLY_META.idle;
  const confidence = ticket.aiAgent?.firstReplyConfidence;
  // Guardia de alcance (#508): al solicitante SIN cuenta sólo se le manda el
  // acuse de recibo cuando la wiki pública no cubre su consulta, o cuando lo que
  // redactó el modelo no era apto para él.
  const guard = ticket.aiAgent?.firstReplyGuard || null;
  const disabled = ticket.aiFirstReplyDisabled === true;

  return (
    <div className={`rounded-xl border p-5 ${card}`} data-testid="ai-first-reply-panel">
      <div className="mb-3 flex items-center justify-between">
        <h2 className={`text-sm font-semibold ${text}`}>Respuesta inmediata</h2>
        <span
          className="rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ color: meta.color, backgroundColor: meta.bg }}
        >
          {meta.label}
        </span>
      </div>

      {state === "sent" ? (
        <dl className={`space-y-1 text-xs ${muted}`} data-testid="ai-first-reply-sent">
          <div className="flex justify-between gap-2">
            <dt>Enviada</dt>
            <dd className={text}>{formatDate(ticket.aiAgent?.firstReplyAt)}</dd>
          </div>
          {ticket.aiAgent?.firstReplyScope && (
            <div className="flex justify-between gap-2">
              <dt>Alcance del solicitante</dt>
              <dd className={text}>
                {ticket.aiAgent.firstReplyScope === "company" ? "Con cuenta y compañía" : "Sin cuenta (limitado)"}
              </dd>
            </div>
          )}
          {typeof confidence === "number" && (
            <div className="flex justify-between gap-2">
              <dt>Confianza del modelo</dt>
              <dd className={text}>{Math.round(confidence * 100)}%</dd>
            </div>
          )}
          {ticket.aiAgent?.firstReplyNeedsInfo && (
            <div className="flex justify-between gap-2">
              <dt>Falta información</dt>
              <dd className={text}>Sí</dd>
            </div>
          )}
          {ticket.aiAgent?.firstReplyIsCodeIssue && (
            <div className="flex justify-between gap-2">
              <dt>¿Parece código?</dt>
              <dd className={text}>Sí</dd>
            </div>
          )}
        </dl>
      ) : state === "failed" ? (
        <p className="rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-500/10">
          No se pudo generar la respuesta automática{ticket.aiAgent?.firstReplyError ? `: ${ticket.aiAgent.firstReplyError}` : "."} El
          ticket sigue esperando una respuesta humana.
        </p>
      ) : (
        <p className={`text-xs ${muted}`}>
          {state === "working"
            ? "Redactando la primera respuesta…"
            : state === "skipped"
              ? "Este ticket no recibió respuesta automática (excluido por la configuración de la mesa o sin texto suficiente)."
              : "Todavía sin respuesta automática."}
        </p>
      )}

      {/* El cierre SÓLO aplica cuando algo salió: antes decía "se publicó … el
          reloj quedó detenido" también en `failed`, `skipped`, `working` e
          `idle`, donde no se publicó nada. */}
      {state === "sent" &&
        (guard ? (
          <p className={`mt-3 text-xs ${muted}`} data-testid="ai-first-reply-guard">
            Al cliente sólo se le envió el <strong>acuse de recibo</strong>
            {guard === "not_covered"
              ? ": no tiene cuenta y su consulta no está cubierta por la documentación pública."
              : ": no tiene cuenta y la redacción del modelo no era apta para él."}{" "}
            El reloj de primera respuesta del SLA <strong>sigue corriendo</strong>: este ticket todavía necesita una
            respuesta humana.
          </p>
        ) : (
          <p className={`mt-3 text-xs ${muted}`} data-testid="ai-first-reply-sla">
            La respuesta se publicó en el hilo del cliente y se envió por correo, firmada como automática.
            El reloj de primera respuesta del SLA quedó detenido.
          </p>
        ))}

      {/* Marca "sin IA" de ESTE ticket (`aiFirstReplyDisabled`): apaga la
          respuesta inmediata para un caso puntual sin tocar la configuración de
          toda la mesa.

          Se muestra SIEMPRE, también con la respuesta ya enviada. Ocultarla en
          `sent` la volvía inalcanzable en la práctica: el disparador contesta a
          los segundos de crearse el ticket, así que nadie llega a tildarla "a
          tiempo". Lo que sí gobierna desde aquí es lo que venga después —un
          ticket reabierto, o cualquier automatismo futuro sobre este caso— y
          queda en la bitácora del ticket. */}
      {onToggleDisabled && (
        <label className={`mt-3 flex items-center gap-2 text-xs ${muted}`}>
          <input
            type="checkbox"
            checked={disabled}
            disabled={saving}
            onChange={(e) => onToggleDisabled(e.target.checked)}
            data-testid="ai-first-reply-disabled-toggle"
          />
          {state === "sent"
            ? "No volver a responder este ticket automáticamente"
            : "No responder este ticket automáticamente"}
        </label>
      )}
      {onToggleDisabled && state === "sent" && (
        <p className={`mt-1 text-[11px] ${muted}`} data-testid="ai-first-reply-disabled-hint">
          La respuesta inmediata sale a los segundos de crearse el ticket. Esta marca no la deshace: evita que este caso
          vuelva a recibir una respuesta automática.
        </p>
      )}
    </div>
  );
}

/**
 * Conversación continua con IA (#509): cuántos turnos lleva el agente en este
 * ticket, cuánto costó y, si se retiró, por qué. Se oculta si la mesa la tiene
 * apagada y el ticket nunca conversó.
 */
export function AiConversationPanel({
  ticket, settings, card, text, muted, saving = false, onResume,
}: {
  ticket: Ticket;
  settings: ServiceDeskSettings;
  card: string;
  text: string;
  muted: string;
  saving?: boolean;
  onResume?: () => void;
}) {
  // Valores por omisión locales (los mismos que DEFAULT_AI_FIRST_REPLY): este
  // panel se pinta en todas las fichas y no debe depender de más exportaciones.
  const cfg = {
    conversationEnabled: settings.aiFirstReply?.conversationEnabled === true,
    conversationMaxTurns: Number(settings.aiFirstReply?.conversationMaxTurns) || 5,
    conversationMaxCostUsd: Number(settings.aiFirstReply?.conversationMaxCostUsd) || 0.5,
  };
  const conv = ticket.aiConversation || null;
  const turns = Number(conv?.turns) || 0;
  if (!cfg.conversationEnabled && !conv) return null;

  const handedOff = Boolean(conv?.handedOffAt);
  // Una persona del equipo contestó en el hilo: el agente se retiró sin
  // traspaso. También se reactiva con el mismo botón.
  const humanOwns = !handedOff && conv?.state === "human_owns";
  const reason = conv?.handoffReason ? AI_CONVERSATION_HANDOFF_LABEL[conv.handoffReason] || conv.handoffReason : null;

  return (
    <div className={`rounded-xl border p-5 ${card}`} data-testid="ai-conversation-panel">
      <div className="mb-3 flex items-center justify-between">
        <h2 className={`text-sm font-semibold ${text}`}>Conversación con IA</h2>
        <span
          className="rounded-full px-2.5 py-1 text-xs font-semibold"
          style={
            handedOff || humanOwns
              ? { color: "#b45309", backgroundColor: "#fef3c7" }
              : { color: "#059669", backgroundColor: "#d1fae5" }
          }
        >
          {handedOff
            ? "Traspasada a una persona"
            : humanOwns
              ? "La lleva una persona"
              : cfg.conversationEnabled
                ? "Activa"
                : "Apagada en la mesa"}
        </span>
      </div>
      <dl className={`space-y-1 text-xs ${muted}`}>
        <div className="flex justify-between gap-2">
          <dt>Turnos del agente</dt>
          <dd className={text} data-testid="ai-conversation-turns">{turns} de {cfg.conversationMaxTurns}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>Costo acumulado</dt>
          <dd className={text}>US$ {(Number(conv?.costUsd) || 0).toFixed(4)} de {cfg.conversationMaxCostUsd.toFixed(2)}</dd>
        </div>
        {conv?.pausedByBot && (
          <div className="flex justify-between gap-2">
            <dt>SLA</dt>
            <dd className={text}>En pausa: el agente espera una respuesta del cliente</dd>
          </div>
        )}
      </dl>
      {(handedOff || humanOwns) && (
        <div className="mt-3" data-testid="ai-conversation-handoff">
          <p className={`text-xs ${muted}`}>
            {handedOff
              ? `El agente dejó de responder en este ticket${reason ? `: ${reason}` : ""}. Hay una nota interna con el resumen del caso.`
              : "Una persona del equipo contestó en el hilo, así que el agente dejó de responder. Reactívalo si quieres devolverle la conversación."}
          </p>
          {onResume && (
            <button
              type="button"
              onClick={onResume}
              disabled={saving}
              className="mt-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium disabled:opacity-60"
            >
              Reactivar la conversación con IA
            </button>
          )}
        </div>
      )}
    </div>
  );
}
